import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Inject, forwardRef } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, throwError } from 'rxjs';
import { permissionsData } from '../model/permissions.model';
import { env, envPython } from 'src/environments/environments';
import { outlineService } from 'src/app/core/auth/outline.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private readonly fastapi_url_base = `${envPython.url_base}`;

  private permissionsSubject = new BehaviorSubject<permissionsData[]>([]);
  public permissions$ = this.permissionsSubject.asObservable();
  // Agregar esta propiedad para almacenar el perfil actual
  private currentProfileId: number | null = null;

  constructor(
    private http: HttpClient,
    @Inject(forwardRef(() => outlineService)) private outlineService: outlineService
  ) { }

  createPermissions(permissions: permissionsData): Observable<{ success: boolean; message: string; data?: any }> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.post<{ success: boolean; message: string; data?: any }>(`${this.fastapi_url_base}/newPermissions`, permissions, { headers }).pipe(
      map(response => {
        if (response.success) {
          const newProfil = new permissionsData(response.data);
          this.refreshPermissions();
          return { success: true, message: 'permisos creado', data: newProfil };
        } else {
          return { success: false, message: 'Error al crear el permisos', data: response.data };
        }
      }),
      catchError(error => {
        console.error('API error:', error);
        return of({ success: false, message: error.message, data: [] });
      })
    );
  }

  //FUNCION PARA MOSTRAR DATOS
  getAllPermissions(id_perfil: number): Observable<{ success: boolean, message: string, data?: permissionsData[] }> {
    this.currentProfileId = id_perfil; // Guardar el ID actual

    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<{ success: boolean, message: string, data: any }>(`${this.fastapi_url_base}/consultarPermisos/${id_perfil}`, { headers }).pipe(
      map(response => {
        if (response.success) {
          const profilingList: permissionsData[] = Array.isArray(response.data) ? response.data.map((item: any) => new permissionsData(item)) : [];
          this.permissionsSubject.next(profilingList);
          return { success: true, message: 'Perfiles encontrados', data: profilingList };
        } else {
          return { success: false, message: 'No hay Perfiles', data: [] };
        }
      }),
      catchError(error => {
        console.error('API error:', error);
        return of({ success: false, message: error.message, data: [] });
      })
    );
  }

  //FUNCION PARA ACTUALIZAR EL PERFIL
  updatePermissions(id: number, permissions: permissionsData): Observable<{ success: boolean; message: string; data?: any }> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.put<{ success: boolean; message: string; data?: any }>(`${this.fastapi_url_base}/actualizarPermiso/${id}`, permissions, { headers }).pipe(
      map(response => {
        if (response.success) {
          this.refreshPermissions();
          return { success: true, message: 'Perfil actualizado', data: response.data };
        } else {
          return { success: false, message: 'Error al actualizar el perfil', data: response.data };
        }
      }),
      catchError(error => {
        console.error('API error:', error);
        return of({ success: false, message: error.message, data: [] });
      })
    );
  }

  deletePermissions(id: number): Observable<any> {
    return this.http.delete<any>(`${this.fastapi_url_base}/deletePermissions/${id}`).pipe(
      map(response => {
        if (response.success) {
          // Emitir el cambio de permisos
          this.refreshPermissions();
          return response;
        } else {
          throw new Error(response.message || 'Error al eliminar el permiso');
        }
      }),
      catchError(error => {
        return throwError(error);
      })
    );
  }

  getAllModulos(): Observable<{ success: boolean; message: string; data?: any }> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<{ success: boolean; message: string; data: any }>(`${this.fastapi_url_base}/consultarModulos`, { headers }).pipe(
      map(response => {
        if (response.success) {
          return { success: true, message: 'Modulos encontrados', data: response.data };
        } else {
          return { success: false, message: 'No hay Modulos', data: [] };
        }
      }),
      catchError(error => {
        console.error('API error:', error);
        return of({ success: false, message: error.message, data: [] });
      })
    );
  }

  getVistasByModulo(idModulo: number): Observable<{ success: boolean; message: string; data?: any }> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<{ success: boolean; message: string; data: any }>(`${this.fastapi_url_base}/consultarVistas/${idModulo}`, { headers }).pipe(
      map(response => {
        if (response.success) {
          return { success: true, message: 'Vistas encontradas', data: response.data };
        } else {
          return { success: false, message: 'No hay Vistas', data: [] };
        }
      }),
      catchError(error => {
        console.error('API error:', error);
        return of({ success: false, message: error.message, data: [] });
      })
    );
  }

  getElementosByVista(idVista: number): Observable<{ success: boolean; message: string; data?: any }> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<{ success: boolean; message: string; data: any }>(`${this.fastapi_url_base}/consultarElementos/${idVista}`, { headers }).pipe(
      map(response => {
        if (response.success) {
          return { success: true, message: 'Elementos encontrados', data: response.data };
        } else {
          return { success: false, message: 'No hay Elementos', data: [] };
        }
      }),
      catchError(error => {
        console.error('API error:', error);
        return of({ success: false, message: error.message, data: [] });
      })
    );
  }

  private getCurrentProfileId(): number | null {
    return this.currentProfileId;
  }

  private refreshPermissions(): void {
    const currentProfileId = this.getCurrentProfileId();
    if (currentProfileId) {
      // Recargar los permisos en este servicio
      this.getAllPermissions(currentProfileId).subscribe({
        next: (response) => {
          // IMPORTANTE: Invalidar y recargar el cache del outlineService
          this.outlineService.recargarDatos(currentProfileId).subscribe({
            next: (outlineResponse) => {
              // Cache actualizado correctamente
            },
            error: (error) => {
              console.error('Error al recargar cache del outlineService:', error);
            }
          });
        },
        error: (error) => {
          console.error('Error al recargar permisos:', error);
        }
      });
    } else {
      this.permissionsSubject.next([]);
    }
  }

  // Método público para forzar recarga completa
  forceRefresh(): void {
    this.refreshPermissions();
  }

  // Método para establecer el perfil actual manualmente
  setCurrentProfile(profileId: number): void {
    this.currentProfileId = profileId;
  }
}
